/* eslint-disable @typescript-eslint/no-unused-vars */
import usePopUpsV2 from "components/PopUpManagerV2/hooks/usePopUpsV2";
import { useEnvContext } from "contexts";
import { useWebStoreContext } from "contexts/WebStoreProvider";
import { FeaturesFlags } from "helpers";
import { useAppDispatch, useLinks, usePopupsState } from "hooks";
import { PBV3Response } from "interfaces";
import { FeatureType } from "interfaces/feature-tracker";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
	useGetFeatureTrackersQuery,
	usePbGetInactiveTrackerMutation
} from "redux/apis";
import { onOpenPopUp, onPBV3change } from "redux/slices";

export const useInitialActions = (isPageVariant: boolean): void => {
	const links = useLinks();
	const navigate = useNavigate();
	const dispatch = useAppDispatch();
	const { pathname } = useLocation();
	const [flag, setFlag] = useState(false);
	const { isFeatureFlagEnabled } = useEnvContext();
	const { stack: popupManagerV2Variants } = usePopUpsV2();
	const { authToken, userTracker } = useWebStoreContext();
	const [pbGetInactiveTracker] = usePbGetInactiveTrackerMutation();
	const {
		popupManagerVariant,
		dialogVariant,
		isSignupChecking,
		isMegaMenuOpen,
		isAnyPopupOpen
	} = usePopupsState();

	// only to get the footer offer copy
	const isAnyPopupOpenExceptDripOfferAndDips =
		(!!popupManagerVariant && popupManagerVariant !== "drip-offer") ||
		!!dialogVariant ||
		popupManagerV2Variants.length > 0;

	const {
		data,
		refetch,
		isSuccess,
		isFetching,
		isError
	}: {
		data?: PBV3Response;
		refetch: () => void;
		isSuccess: boolean;
		isFetching: boolean;
		isError: boolean;
	} = useGetFeatureTrackersQuery(
		{
			FeatureType: FeatureType.POIS_BOUNTY_V3,
			isAuthenticated: !!userTracker
			//activeOnly: false
		},
		{
			skip:
				!isFeatureFlagEnabled(FeaturesFlags.POIS_BOUNTY_V2) ||
				!authToken?.value ||
				isAnyPopupOpenExceptDripOfferAndDips ||
				isPageVariant ||
				document.visibilityState === "hidden",
			refetchOnFocus: true,
			pollingInterval: 10 * 1000
		}
	);

	const getInactiveTrackers = async () => {
		if (isPageVariant) {
			return;
		}
		// completed events
		await pbGetInactiveTracker({
			activeOnly: false
		})
			.unwrap()
			.then((res: PBV3Response) => {
				if (res?.trackersData) {
					const events = Object.values(res?.trackersData || {}).filter(
						(ts) => ts?.completedDate
					);
					const event =
						events.length === 1
							? events[0]
							: events.sort((a, b) => {
									if (a?.completedDate && b?.completedDate) {
										return (
											new Date(b?.completedDate).getTime() -
											new Date(a?.completedDate).getTime()
										);
									}
									return 0;
							  })[0] || undefined;
					const latestCompletedCycle = event?.cycleTrackers?.[0];

					dispatch(
						onPBV3change({
							data,
							tracker: latestCompletedCycle,
							trackers: event,
							lp: latestCompletedCycle?.loyaltyPoints || 0,
							cycleTrackers: event?.cycleTrackers,
							rewards: latestCompletedCycle?.rewardClaimStatuses || []
						})
					);
				} else {
					dispatch(onPBV3change({ eventActive: false }));
					if (pathname === links.pois_bounty.path) {
						navigate(links.home.path);
					}
				}
			});
	};

	useEffect(() => {
		if (data?.success && !isPageVariant) {
			if (data?.trackersData) {
				// verify if trackers are available
				const trackersV3 = Object.values(data.trackersData).filter(
					(ts) => !ts.completedDate
				);
				// if trackers, select user event for starters. If only one tracker use that
				const activeEvent =
					Object.values(trackersV3).length > 1
						? trackersV3?.find((e) => e.eventType === "user")
						: trackersV3?.slice(-1)?.[0];
				// latest tracker with completed popup shown
				const lt = activeEvent?.userData?.cycleData?.findLast(
					(t) => t.metadata?.popupDisplayed === "true"
				);
				// latest trackers cycle after the one with completed popup shown
				const lc = activeEvent?.cycleTrackers?.find(
					(t) => t.cycleIndex === Number(lt?.cycleIndex ?? -1) + 1
				);
				// always show the latest tracker after the one with completed popup shown,
				// unless event ended and show the last tracker
				const latestCycle = lc || activeEvent?.cycleTrackers?.[0];

				if (activeEvent) {
					dispatch(
						onPBV3change({
							data,
							tracker: latestCycle,
							trackers: activeEvent,
							lp: latestCycle?.loyaltyPoints || 0,
							cycleTrackers: activeEvent?.cycleTrackers,
							rewards: latestCycle?.rewardClaimStatuses || []
						})
					);
				} else {
					getInactiveTrackers();
				}
			} else {
				getInactiveTrackers();
			}
		}
	}, [data]);

	useEffect(() => {
		if (!!userTracker && !isPageVariant) {
			const activeTrackers = Object.values(data?.trackersData || {}).filter(
				(t) => t.canContribute
			);
			if (
				Object.keys(activeTrackers)?.length < 2 ||
				!Object.values(data?.trackersData || [])?.find(
					(t) => t?.eventType === "club"
				) ||
				isAnyPopupOpen ||
				isSignupChecking ||
				isFetching ||
				flag ||
				isMegaMenuOpen
			) {
				return;
			}
			setFlag(true);
			dispatch(onOpenPopUp("pb-v3-select-event"));
		}
	}, [
		userTracker,
		isAnyPopupOpen,
		data,
		isSignupChecking,
		isFetching,
		flag,
		isMegaMenuOpen
	]);
};
